/**
 * Pure mapping between our own chat state (`useAgentChat`'s
 * `AgentChatMessage[]` and its raw `AgentStreamEvent`s) and `@mui/x-chat`'s
 * message/chunk model, so `AgentChatBox` can render the conversation with
 * MUI X Chat while `useAgentChat` keeps owning the fetch, the reducer and
 * the side effects (refresh-after-write, navigation, message capping, view
 * memory) — see `AgentChatBox.js`.
 *
 * `useAgentChat.send`'s own reducer dispatch and this module's translation
 * both read the *same* underlying `AgentStreamEvent`s (the `onEvent` param
 * threaded through `send`), so the two never drift.
 */
import type {
  ChatAdapter,
  ChatDynamicToolMessagePart,
  ChatMessage,
  ChatMessageChunk,
  ChatMessagePart,
} from "@mui/x-chat/headless";
import {
  uploadAgentAttachment,
  type UploadedAgentAttachment,
} from "./AgentAttachments.js";
import type {
  AgentChatResult,
  AgentStreamEvent,
  AgentTierBreakdown,
  AgentToolCall,
} from "./agentStream.js";
import type { PageContext } from "./pageContext.js";
import type { AgentChatMessage, AgentSendOutcome } from "./useAgentChat.js";
import type { AgentViewSpec } from "./views/registry.js";

/** The data part carrying a view the assistant composed (rendered via `views/registry.js`). */
export const VIEW_PART = "data-agent-view";
/** The data part carrying the stored answer id, model and cost. */
export const META_PART = "data-agent-meta";
/**
 * Marks the text right before it as withdrawn: text streamed while the
 * model wrote it (`text_delta`) that did not become the answer after all
 * (the backend retried or corrected the round, `text_discard`). The chat cannot
 * take streamed text back, so it is hidden instead.
 */
export const DISCARD_PART = "data-agent-discard";

export type AgentMetaPart = {
  messageId: string | null;
  model: string | null;
  costUsd: number | null;
  promptTokens: number | null;
  completionTokens: number | null;
  tiers?: AgentTierBreakdown;
  escalatedAtIteration?: number | null;
};

/**
 * Collapses whitespace runs to a single space and trims, so comparing the
 * streamed text against the backend's finished reply is not thrown off by
 * where exactly a paragraph break landed (the backend trims a kept round's
 * text and joins it to the next round's with its own "\n\n").
 */
function normalizeChatWhitespace(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}

/** Whether the text part at `index` was withdrawn by a later discard marker. */
export function isDiscardedText(message: ChatMessage, index: number): boolean {
  return message.parts.slice(index + 1).some(p => p.type === DISCARD_PART);
}

/**
 * Strips the leading paragraph break off a text part's content. A text part
 * only starts where a visual block begins (message start, or right after a
 * tool/view/discard entry), so its leading "\n\n" is the previous block's
 * separator and would double into an empty paragraph.
 */
export function stripLeadingBreak(text: string): string {
  return text.replace(/^\n+/, "");
}

/**
 * The message's text as shown: its text parts without withdrawn drafts,
 * one paragraph break between them. A part after a tool entry starts with
 * the paragraph break the backend streams between two tool rounds' texts
 * (`AgentTextStream`); left in, it doubled up with the join into an empty
 * paragraph, so the text read aloud no longer matched the finished reply.
 */
export function visibleText(message: ChatMessage): string {
  return message.parts
    .flatMap((part, index) =>
      part.type === "text" && !isDiscardedText(message, index)
        ? [stripLeadingBreak(part.text)]
        : []
    )
    .join("\n\n");
}

/**
 * A conversation-list entry's subtitle: its created date through the
 * platform's locale-aware formatter (`useFormatting().getFormattedDateTime`,
 * not raw `Date.prototype.toLocaleString()`) plus its message count.
 */
export function conversationListSubtitle(
  conversation: { createdAt: string; messageCount: number },
  getFormattedDateTime: (value: string) => string
): string {
  return `${getFormattedDateTime(conversation.createdAt)} · ${conversation.messageCount}`;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Paging and switches that say nothing about what a call looked for. */
const QUIET_INPUTS = new Set(["limit", "offset", "includeEliminated"]);
const MAX_LABEL = 70;

/**
 * Turns a tool call into the title its chat line starts with — the
 * translated `labelKey`, or the tool's name without one
 * (`useAgentToolTitle`, `agentToolTitle.ts`). Handed in by `AgentChatBox`
 * and read when the line is built, so it is in the language shown then.
 */
export type AgentToolTitle = (call: {
  name: string;
  labelKey?: string | null;
}) => string;

/**
 * A tool call's line in the chat: its title (`AgentToolTitle`) plus what it
 * searched for or changed ("Searching quotes · Berlin, 10000"), so two calls
 * of the same tool can be told apart. Ids and paging are left out.
 */
export function toolLabel(name: string, input: unknown): string {
  if (typeof input !== "object" || input === null) return name;
  const values = Object.entries(input).flatMap(([key, value]) => {
    if (QUIET_INPUTS.has(key)) return [];
    const scalars = Array.isArray(value) ? value : [value];
    return scalars.flatMap(v =>
      (typeof v === "string" && v.trim() !== "" && !UUID.test(v)) ||
      typeof v === "number"
        ? [String(v)]
        : []
    );
  });
  if (values.length === 0) return name;
  const label = `${name} · ${values.join(", ")}`;
  return label.length > MAX_LABEL ? `${label.slice(0, MAX_LABEL - 1)}…` : label;
}

/**
 * A stored tool call's `position` clamped to `[0, contentLength]`, or `null`
 * when it carries no numeric one (history stored before positions).
 */
function toolCallPosition(
  call: AgentToolCall,
  contentLength: number
): number | null {
  const { position } = call;
  if (typeof position !== "number" || !Number.isFinite(position)) return null;
  return Math.min(Math.max(Math.trunc(position), 0), contentLength);
}

/**
 * A stored answer's `content` split at its tool calls' positions, in order:
 * each text part followed by the calls sitting right after it — so the tool
 * entries stand between the answer's parts like they did while it streamed.
 * Calls without a position go before the whole answer; calls sharing a
 * position (one model round) share its gap in their stored order. The
 * whitespace at a split (the paragraph break between two rounds' text) is
 * dropped; a part with nothing left is left out.
 */
function answerLayout(
  content: string,
  toolCalls: readonly AgentToolCall[]
): Array<
  | { kind: "text"; text: string }
  | { kind: "tool"; call: AgentToolCall; index: number }
> {
  const placed = toolCalls
    .map((call, index) => ({
      call,
      index,
      at: toolCallPosition(call, content.length) ?? 0,
    }))
    // Stable: calls of one gap keep their stored (call) order.
    .sort((a, b) => a.at - b.at);
  const layout: ReturnType<typeof answerLayout> = [];
  let start = 0;
  let textShown = false;
  const pushText = (end: number) => {
    let text = content.slice(start, end);
    if (start > 0) text = text.trimStart();
    if (end < content.length) text = text.trimEnd();
    if (text !== "") {
      layout.push({ kind: "text", text });
      textShown = true;
    }
    start = end;
  };
  for (const { call, index, at } of placed) {
    if (at > start) pushText(at);
    layout.push({ kind: "tool", call, index });
  }
  pushText(content.length);
  // An empty answer still gets its (empty) text part, as before positions.
  if (!textShown) layout.push({ kind: "text", text: content });
  return layout;
}

let syntheticId = 0;
/** A stable-enough id for a stored message without one (a user turn). */
function idFor(message: AgentChatMessage, index: number): string {
  if (message.id) return message.id;
  syntheticId += 1;
  return `msg-${index}-${syntheticId}`;
}

/**
 * A stored/sent conversation (`useAgentChat`'s `AgentChatMessage[]`) in MUI
 * X Chat's `ChatMessage[]` shape — used to seed the box from history
 * (`createAgentChatAdapter`'s `listMessages`) or from an already-answered
 * turn.
 */
export function toChatMessages(
  messages: readonly AgentChatMessage[],
  toolTitle: AgentToolTitle
): ChatMessage[] {
  return messages.map((m, index): ChatMessage => {
    const id = idFor(m, index);
    if (m.role === "user")
      return { id, role: "user", parts: [{ type: "text", text: m.content }] };
    const parts: ChatMessagePart[] = [];
    for (const entry of answerLayout(m.content, m.toolCalls ?? [])) {
      if (entry.kind === "text") {
        parts.push({ type: "text", text: entry.text });
        continue;
      }
      const { call, index: i } = entry;
      const failed = call.resultPreview.startsWith("ERROR");
      const invocation: ChatDynamicToolMessagePart["toolInvocation"] = {
        toolCallId: `${id}-tool-${i}`,
        toolName: toolLabel(toolTitle(call), call.input),
        input: call.input,
        state: failed ? "output-error" : "output-available",
        ...(failed
          ? { errorText: call.resultPreview }
          : { output: call.resultPreview }),
      };
      parts.push({ type: "dynamic-tool", toolInvocation: invocation });
    }
    for (const action of m.uiActions ?? [])
      if (action.type === "view")
        parts.push({ type: VIEW_PART, data: action.view });
    const meta: AgentMetaPart = {
      messageId: m.id ?? null,
      model: m.model ?? null,
      costUsd: m.costUsd ?? null,
      promptTokens: m.promptTokens ?? null,
      completionTokens: m.completionTokens ?? null,
      tiers: m.tiers,
      escalatedAtIteration: m.escalatedAtIteration ?? null,
    };
    parts.push({ type: META_PART, data: meta });
    return { id, role: "assistant", parts };
  });
}

const isView = (data: unknown): data is AgentViewSpec =>
  typeof data === "object" && data !== null && "component" in data;

const isMeta = (data: unknown): data is AgentMetaPart =>
  typeof data === "object" && data !== null && "messageId" in data;

/** Reads a `data-agent-view` part's payload, or `null` when the part isn't one. */
export function viewOf(part: ChatMessagePart): AgentViewSpec | null {
  return part.type === VIEW_PART && "data" in part && isView(part.data)
    ? part.data
    : null;
}

/** Reads a `data-agent-meta` part's payload, or `null` when the part isn't one. */
export function metaOf(message: ChatMessage): AgentMetaPart | null {
  const part = message.parts.find(p => p.type === META_PART);
  return part && "data" in part && isMeta(part.data) ? part.data : null;
}

/**
 * The cost `MetaPart` shows for a stored answer: a live override (keyed by
 * the message's stored id, `AgentPanel`'s `costOverrideByMessageId`, kept in
 * sync with `chat.messages`) when one is known, otherwise the frozen
 * `meta.costUsd` that `AgentChatBox`'s `initialMessages` seeded this
 * message's `META_PART` with at mount. `initialMessages` is deliberately
 * never re-derived from `chat.messages` (MUI X Chat warns on a changed
 * "initial" prop), so a stored answer's cost booked after the fact — a
 * replayed reading's speech usage, posted to the backend once it settles —
 * would otherwise never reach the rendered answer's own caption, even
 * though the session total (computed live from `chat.messages`) does
 * change.
 */
export function resolveAnswerCostUsd(
  meta: Pick<AgentMetaPart, "costUsd">,
  override: number | null | undefined
): number | null {
  return override === undefined ? meta.costUsd : override;
}

/**
 * Translates one turn's stream events into MUI X Chat chunks, in order —
 * the live answer's `ReadableStream` (`createAgentChatAdapter`) and a turn
 * reopened from the background (`runningAnswerMessage`) both go through
 * this, so the two always look the same. `emit` receives each chunk;
 * `conversation` and `error` events produce none.
 */
export function createAgentChunkTranslator(opts: {
  emit: (chunk: ChatMessageChunk) => void;
  toolTitle: AgentToolTitle;
  emptyReplyText: () => string;
}): (event: AgentStreamEvent) => void {
  const { emit } = opts;
  // Matched to their `tool_done` by the model's own `callId`, not by
  // name/order: two calls to the same tool in one round can finish in the
  // opposite order from their starts.
  const open: Array<{ id: string; callId: string }> = [];
  // The text streaming in right now, until it ends as the answer or is withdrawn.
  let draft: { id: string; text: string } | null = null;
  // Earlier rounds' text this turn that a tool start ended as *kept* (the
  // backend keeps a round's text before its tool calls): closed text parts
  // sitting before the tool parts that followed them. Joined with a
  // still-open draft, this is everything shown so far.
  const keptSegments: string[] = [];
  const endDraft = (discard: boolean) => {
    if (!draft) return;
    emit({ type: "text-end", id: draft.id });
    if (discard) emit({ type: DISCARD_PART, data: {} });
    draft = null;
  };

  return event => {
    if (event.type === "tool_start") {
      // A draft still open here belongs to a round the backend kept (a
      // withdrawn round's discard comes before its tools):
      // close it as kept and remember it, so the next round's text starts a
      // new part *after* this tool instead of growing the part before it —
      // which left the tool entry stuck after all the text.
      if (draft) {
        emit({ type: "text-end", id: draft.id });
        keptSegments.push(draft.text);
        draft = null;
      }
      const id = crypto.randomUUID();
      open.push({ id, callId: event.callId });
      emit({
        type: "tool-input-available",
        toolCallId: id,
        toolName: toolLabel(opts.toolTitle(event), event.input),
        input: event.input,
      });
    } else if (event.type === "tool_done") {
      const index = open.findIndex(o => o.callId === event.callId);
      const call = index >= 0 ? open.splice(index, 1)[0] : undefined;
      if (!call) return;
      const entry: AgentToolCall = event.entry;
      emit(
        entry.resultPreview.startsWith("ERROR")
          ? {
              type: "tool-output-error",
              toolCallId: call.id,
              errorText: entry.resultPreview,
            }
          : {
              type: "tool-output-available",
              toolCallId: call.id,
              output: entry.resultPreview,
            }
      );
    } else if (event.type === "text_delta") {
      if (!draft) {
        draft = { id: crypto.randomUUID(), text: "" };
        emit({ type: "text-start", id: draft.id });
      }
      draft.text += event.delta;
      emit({ type: "text-delta", id: draft.id, delta: event.delta });
    } else if (event.type === "text_discard") {
      endDraft(true);
    } else if (event.type === "result") {
      const reply = event.reply || opts.emptyReplyText();
      // The finished answer (links added, notes removed) replaces
      // everything streamed this turn — kept segments of earlier
      // show_view-only rounds plus the open draft — unless it is, up to
      // whitespace, the same text. One discard marker withdraws all of
      // those text parts.
      const streamedSoFar = [...keptSegments, draft?.text ?? ""].join("");
      const kept =
        normalizeChatWhitespace(streamedSoFar) ===
        normalizeChatWhitespace(reply);
      const shownBefore = draft !== null || keptSegments.length > 0;
      endDraft(false);
      if (!kept) {
        if (shownBefore) emit({ type: DISCARD_PART, data: {} });
        const textId = crypto.randomUUID();
        emit({ type: "text-start", id: textId });
        emit({ type: "text-delta", id: textId, delta: reply });
        emit({ type: "text-end", id: textId });
      }
      for (const action of event.uiActions)
        if (action.type === "view")
          emit({ type: VIEW_PART, data: action.view });
      const meta: AgentMetaPart = {
        messageId: event.messageId,
        model: event.model,
        costUsd: event.costUsd,
        promptTokens: event.promptTokens,
        completionTokens: event.completionTokens,
        tiers: event.tiers,
        escalatedAtIteration: event.escalatedAtIteration ?? null,
      };
      emit({ type: META_PART, data: meta });
    }
  };
}

/** Folds a turn's chunks (`createAgentChunkTranslator`) into one message's parts. */
export function chunksToParts(
  chunks: readonly ChatMessageChunk[]
): ChatMessagePart[] {
  const parts: ChatMessagePart[] = [];
  const textIndexById = new Map<string, number>();
  const toolIndexById = new Map<string, number>();
  const updateTool = (
    toolCallId: string,
    update: (
      invocation: ChatDynamicToolMessagePart["toolInvocation"]
    ) => ChatDynamicToolMessagePart["toolInvocation"]
  ) => {
    const index = toolIndexById.get(toolCallId);
    const part = index === undefined ? undefined : parts[index];
    if (index === undefined || part?.type !== "dynamic-tool") return;
    parts[index] = {
      type: "dynamic-tool",
      toolInvocation: update(part.toolInvocation),
    };
  };
  for (const chunk of chunks) {
    switch (chunk.type) {
      case "text-start":
        textIndexById.set(chunk.id, parts.length);
        parts.push({ type: "text", text: "" });
        break;
      case "text-delta": {
        const index = textIndexById.get(chunk.id);
        const part = index === undefined ? undefined : parts[index];
        if (index !== undefined && part?.type === "text")
          parts[index] = { type: "text", text: part.text + chunk.delta };
        break;
      }
      case "tool-input-available":
        toolIndexById.set(chunk.toolCallId, parts.length);
        parts.push({
          type: "dynamic-tool",
          toolInvocation: {
            toolCallId: chunk.toolCallId,
            toolName: chunk.toolName,
            input: chunk.input,
            state: "input-available",
          },
        });
        break;
      case "tool-output-available":
        updateTool(chunk.toolCallId, invocation => ({
          ...invocation,
          state: "output-available",
          output: chunk.output,
        }));
        break;
      case "tool-output-error":
        updateTool(chunk.toolCallId, invocation => ({
          ...invocation,
          state: "output-error",
          errorText: chunk.errorText,
        }));
        break;
      default:
        if (
          (chunk.type === DISCARD_PART ||
            chunk.type === VIEW_PART ||
            chunk.type === META_PART) &&
          "data" in chunk
        )
          parts.push({ type: chunk.type, data: chunk.data });
    }
  }
  return parts;
}

/**
 * A turn reopened from the background as the answer being written: its
 * progress so far (streaming), or — once `result` is set — the finished
 * answer. Pushed into the chat box in place of the live stream the box that
 * sent the turn had (see `AgentChatBox`'s `BackgroundTurnSync`).
 */
export function runningAnswerMessage(
  turn: {
    key: string;
    /** The id the sending box streamed the answer under (`AgentBackgroundTurn.messageId`). */
    messageId: string | null;
    events: readonly AgentStreamEvent[];
    result: AgentChatResult | null;
  },
  toolTitle: AgentToolTitle,
  emptyReplyText: () => string
): ChatMessage {
  const chunks: ChatMessageChunk[] = [];
  const translate = createAgentChunkTranslator({
    emit: chunk => chunks.push(chunk),
    toolTitle,
    emptyReplyText,
  });
  turn.events.forEach(translate);
  if (turn.result) translate({ type: "result", ...turn.result });
  return {
    // The sending box's own id for this answer when known: a reading started
    // there (`useAgentSpeech`, keyed by the chat message id) is still this
    // message after a switch away and back.
    id: turn.messageId ?? `background-${turn.key}`,
    role: "assistant",
    status: turn.result ? "sent" : "streaming",
    parts: chunksToParts(chunks),
  };
}

/** The user's newest message as plain text (attachments travel separately). */
function plainTextOf(message: ChatMessage): string {
  return message.parts
    .flatMap(part => (part.type === "text" ? [part.text] : []))
    .join("\n\n");
}

/**
 * Builds the `ChatAdapter` MUI X Chat's `ChatBox` drives: `sendMessage`
 * uploads any attachments, then translates our own GraphQL-subscription
 * stream (read by `opts.send`, i.e. `useAgentChat().send`) into MUI X
 * Chat's chunk protocol, live, as each event arrives — the same events
 * `useAgentChat`'s reducer dispatches from in parallel, so the box's own
 * message store and `useAgentChat`'s state (used for the next turn's
 * history, view memory and refresh-after-write) never disagree about what
 * happened.
 */
export function createAgentChatAdapter(opts: {
  send: (
    content: string,
    options: {
      context: PageContext;
      attachments?: UploadedAgentAttachment[];
      confirm?: boolean;
      /** The id this stream gives the answer (its "start" chunk). */
      messageId?: string;
      onAccepted?: () => void;
      onEvent?: (event: AgentStreamEvent) => void;
    }
  ) => Promise<AgentSendOutcome>;
  /** The page the user is looking at when sending. */
  context: () => PageContext;
  /** Whether this send answers "yes" to the shown proposal (the "Ja" button); read once. */
  takeConfirm: () => boolean;
  /** Uploads one attachment's bytes directly to the fileshare bucket (`requestAgentAttachmentUploadUrl`, see `AgentChatBox.tsx`), resolving its `uploadKey`. */
  requestAttachmentUploadUrl: (input: {
    fileName: string;
    mediaType: string;
    fileSizeBytes: number;
  }) => Promise<{ uploadUrl: string; uploadKey: string }>;
  /** Shown for an empty answer; read when the answer arrives, so it is in the language shown then. */
  emptyReplyText: () => string;
  /** A tool call's title (`AgentToolTitle`); read when its `tool_start` arrives, so it is in the language shown then. */
  toolTitle: AgentToolTitle;
  /**
   * The backend refused this send because a turn is still running
   * (`AGENT_CONFLICT`, `useAgentChat`'s `"conflict"` outcome): nothing is
   * streamed, and the caller takes the optimistic user message back out of
   * the chat and puts `text` back into the composer.
   */
  onConflict: (refused: { messageId: string; text: string }) => void;
  /**
   * The turn failed without a stored answer (`useAgentChat`'s `"failed"`
   * outcome): the caller takes the optimistic user message and the failed
   * assistant message out of the chat and puts `text` and the attachments
   * back into the composer.
   * Called once the chat has read the stream's failure (a macrotask later), so
   * the removal is the last word — the chat itself marks the messages failed
   * and would re-add the assistant message from a not yet read `start` chunk.
   */
  onFailed?: (failed: {
    messageId: string;
    assistantMessageId: string;
    text: string;
    /** The failed message's attachments, to be added to the composer again. */
    files: File[];
  }) => void;
  /**
   * Called synchronously as the send starts — inside the composer's own
   * submit, before anything is awaited — e.g. to end a dictation right
   * there, so nothing the microphone still picks up lands in the composer
   * the submit just emptied.
   */
  onSend?: () => void;
}): ChatAdapter {
  return {
    async sendMessage(input) {
      opts.onSend?.();
      const text = plainTextOf(input.message);
      // Read before the (possibly rejecting) attachment uploads: a one-shot
      // flag must be consumed exactly once per send, not leak into the next
      // message when an upload fails.
      const confirm = opts.takeConfirm();
      const attachments = await Promise.all(
        (input.attachments ?? []).map(a =>
          uploadAgentAttachment(a.file, opts.requestAttachmentUploadUrl)
        )
      );

      // Settles once the turn started streaming (`onAccepted`: its first
      // event) or the send ended any other way, `false` only for a refusal
      // (`AGENT_CONFLICT`, or a raced send's "still working" unavailable
      // event — both arrive before any other event) — so the stream is handed to
      // MUI X Chat as soon as the answer can start, never only at its end,
      // and a refused send never reaches the chat as an (empty) answer.
      let settleAcceptance: (accepted: boolean) => void = () => undefined;
      const acceptance = new Promise<boolean>(resolve => {
        settleAcceptance = resolve;
      });

      const stream = new ReadableStream<ChatMessageChunk>({
        async start(controller) {
          const messageId = crypto.randomUUID();
          // Once the chat aborted this send (a new chat, another
          // conversation, the box unmounting), the turn keeps running in the
          // background (`agentBackgroundTurns.ts`) but nothing more goes into
          // this stream — the chat has stopped reading it.
          let ended = false;
          const enqueue = (chunk: ChatMessageChunk) => {
            if (!ended) controller.enqueue(chunk);
          };
          const end = (last?: ChatMessageChunk) => {
            if (ended) return;
            if (last) controller.enqueue(last);
            ended = true;
            controller.close();
          };
          const onAbort = () => {
            settleAcceptance(true);
            end({ type: "abort", messageId });
          };
          enqueue({ type: "start", messageId });
          if (input.signal.aborted) {
            onAbort();
            return;
          }
          input.signal.addEventListener("abort", onAbort, { once: true });
          const translate = createAgentChunkTranslator({
            emit: enqueue,
            toolTitle: opts.toolTitle,
            emptyReplyText: opts.emptyReplyText,
          });
          let streamError: Error | null = null;
          let failed = false;

          try {
            const outcome = await opts.send(text, {
              context: opts.context(),
              attachments,
              confirm,
              messageId,
              onAccepted: () => settleAcceptance(true),
              onEvent: (event: AgentStreamEvent) => {
                if (event.type === "error")
                  streamError = new Error(event.error);
                else translate(event);
              },
            });
            input.signal.removeEventListener("abort", onAbort);
            if (ended) return;
            if (outcome === "conflict") {
              // This stream is never handed out (see `acceptance` below).
              settleAcceptance(false);
              end();
              return;
            }
            settleAcceptance(true);
            failed = outcome === "failed";
            if (failed && !streamError)
              streamError = new Error("The assistant could not answer.");
            if (streamError) throw streamError;
            end({ type: "finish", messageId });
          } catch (err) {
            input.signal.removeEventListener("abort", onAbort);
            settleAcceptance(true);
            if (ended) return;
            ended = true;
            controller.error(err);
            if (failed)
              setTimeout(
                () =>
                  opts.onFailed?.({
                    messageId: input.message.id,
                    assistantMessageId: messageId,
                    text,
                    files: (input.attachments ?? []).map(a => a.file),
                  }),
                0
              );
          }
        },
      });

      if (await acceptance) return stream;
      opts.onConflict({ messageId: input.message.id, text });
      return new ReadableStream<ChatMessageChunk>({
        start: controller => controller.close(),
      });
    },
  };
}
